import { CYCLE_DAY_TEST_START, CYCLE_DAY_TEST_END, CYCLE_DAY_TEST_UNDERLINE_START, CYCLE_DAY_TEST_UNDERLINE_END } from '../data/constants';

/**
 * CalendarDayButtonSimulated - Non-interactive calendar day button for help menu
 * Simplified version that accepts day data directly
 */
export default function CalendarDayButtonSimulated({ dayData, dayNum }) {
  const { ipData, maxTestValue } = dayData || {};

  // Get day type
  function getDayType(dayData) {
    if (!dayData) return 'normal';
    const { ipData } = dayData;
    if (ipData?.peak || ipData?.peakTest) return 'peak-test';
    if (ipData?.peakPredicted) return 'peak-predicted';
    if (ipData?.predictedOvulation && ipData?.inseminationDay) return 'predicted-ovulation-insemination';
    if (ipData?.predictedOvulation) return 'predicted-ovulation';
    if (ipData?.predictedPeriod) return 'predicted-period';
    if (ipData?.inseminationDay) return 'insemination';
    return 'normal';
  }

  const TYPE_CLASSES = {
    'peak-test': 'bg-purple-500 text-white font-semibold',
    'peak-predicted': 'bg-primary/20 text-primary font-semibold',
    'predicted-ovulation': 'border-2 border-purple-500',
    'predicted-ovulation-insemination': 'border-2 border-purple-500',
    'predicted-period': 'border-2 border-destructive',
  };

  const TYPE_LABELS = {
    'peak-test': 'Peak',
    'peak-predicted': 'Pred. Ov.',
  };

  const type = getDayType(dayData);
  const cd = ipData?.cd;
  const hasTestValue = maxTestValue !== undefined && maxTestValue !== null;

  // Calculate purple background opacity based on max test value
  const testOpacity = hasTestValue
    ? Math.min(1, Math.max(0.05, maxTestValue / 2))
    : 0;

  let classes = 'flex h-full w-full flex-col items-center justify-center rounded-full text-sm';
  classes += ` ${TYPE_CLASSES[type] ?? ''}`;

  const backgroundStyle = hasTestValue
    ? { backgroundColor: `rgba(147, 51, 234, ${testOpacity})` }
    : undefined;

  // Predicted ovulation + insemination day
  if (type === 'predicted-ovulation-insemination') {
    return (
      <div className="w-[60px] h-[60px] flex items-center justify-center">
        <div className="relative w-full h-full flex items-center justify-center">
          <div className="absolute inset-0 flex items-center justify-center rounded-full border-2 border-[#bfdbfe] p-[2px]">
            <div className="w-full h-full flex items-center justify-center rounded-full border-2 border-[#fce7f3] p-[2px]">
              <button className={classes} style={backgroundStyle} disabled>
                {dayNum}
                {TYPE_LABELS[type] && <span className="text-[7px] font-semibold leading-none">{TYPE_LABELS[type]}</span>}
                {cd && type !== 'peak-test' && type !== 'peak-predicted' && (
                  <span className={`text-[7px] leading-none font-black ${type === 'predicted-ovulation' ? (hasTestValue ? 'text-black' : 'text-purple-500') : (type === 'predicted-period' ? 'text-destructive' : (cd === 1 ? 'text-orange-500' : 'text-muted-foreground'))}`}>
                    {type === 'predicted-ovulation' ? 'P. OV' : (type === 'predicted-period' ? 'Start' : (cd === 1 ? 'Start' : `CD ${cd}`))}
                  </span>
                )}
                {cd >= CYCLE_DAY_TEST_START && cd <= CYCLE_DAY_TEST_END && (
                  <span className={`text-[7px] leading-none font-black text-green-500 ${cd >= CYCLE_DAY_TEST_UNDERLINE_START && cd <= CYCLE_DAY_TEST_UNDERLINE_END ? 'underline underline-offset-[2px]' : ''}`}>Test</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Insemination day
  if (type === 'insemination') {
    return (
      <div className="w-[60px] h-[60px] flex items-center justify-center">
        <div className="relative w-full h-full flex items-center justify-center">
          <div className="absolute inset-0 flex items-center justify-center rounded-full border-2 border-[#bfdbfe] p-[2px]">
            <button className={`${classes} border-2 border-[#fce7f3]`} style={backgroundStyle} disabled>
              {dayNum}
              {TYPE_LABELS[type] && <span className="text-[7px] font-semibold leading-none">{TYPE_LABELS[type]}</span>}
              {cd && type !== 'peak-test' && type !== 'peak-predicted' && (
                <span className={`text-[7px] leading-none font-black ${type === 'predicted-ovulation' ? (hasTestValue ? 'text-black' : 'text-purple-500') : (type === 'predicted-period' ? 'text-destructive' : (cd === 1 ? 'text-orange-500' : 'text-muted-foreground'))}`}>
                  {type === 'predicted-ovulation' ? 'P. OV' : (type === 'predicted-period' ? 'Start' : (cd === 1 ? 'Start' : `CD ${cd}`))}
                </span>
              )}
              {cd >= CYCLE_DAY_TEST_START && cd <= CYCLE_DAY_TEST_END && (
                <span className={`text-[7px] leading-none font-black text-green-500 ${cd >= CYCLE_DAY_TEST_UNDERLINE_START && cd <= CYCLE_DAY_TEST_UNDERLINE_END ? 'underline underline-offset-[2px]' : ''}`}>Test</span>
              )}
              <span className="absolute -right-0.5 -bottom-0.5 text-[10px] leading-none">🍀</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal day
  return (
    <div className="w-[60px] h-[60px] flex items-center justify-center">
      <button className={classes} style={backgroundStyle} disabled>
        {dayNum}
        {TYPE_LABELS[type] && <span className="text-[7px] font-semibold leading-none">{TYPE_LABELS[type]}</span>}
        {cd && type !== 'peak-test' && type !== 'peak-predicted' && (
          <span className={`text-[7px] leading-none font-black ${type === 'predicted-ovulation' ? (hasTestValue ? 'text-black' : 'text-purple-500') : (type === 'predicted-period' ? 'text-destructive' : (cd === 1 ? 'text-orange-500' : 'text-muted-foreground'))}`}>
            {type === 'predicted-ovulation' ? 'P. OV' : (type === 'predicted-period' ? 'Start' : (cd === 1 ? 'Start' : `CD ${cd}`))}
          </span>
        )}
        {cd >= CYCLE_DAY_TEST_START && cd <= CYCLE_DAY_TEST_END && (
          <span className={`text-[7px] leading-none font-black text-green-500 ${cd >= CYCLE_DAY_TEST_UNDERLINE_START && cd <= CYCLE_DAY_TEST_UNDERLINE_END ? 'underline underline-offset-[2px]' : ''}`}>Test</span>
        )}
      </button>
    </div>
  );
}
